<template>
  <div class="simple-editor" :class="{ 'is-dark': $store.getters.isDark }">
    <div class="simple-editor__toolbar">
      <button
        v-for="tool in tools"
        :key="tool.command"
        type="button"
        class="simple-editor__btn"
        :title="tool.title"
        @mousedown.prevent="execCommand(tool.command, tool.value)"
      >
        <v-icon size="18" :color="iconColor">{{ tool.icon }}</v-icon>
      </button>
    </div>
    <div
      ref="editor"
      class="simple-editor__content"
      contenteditable="true"
      :data-placeholder="placeholder"
      @input="onInput"
      @blur="onInput"
      @paste="onPaste"
    ></div>
  </div>
</template>

<script>
export default {
  name: "SimpleTextEditor",
  props: {
    value: { type: String, default: "" },
    placeholder: { type: String, default: "" },
  },
  data() {
    return {
      tools: [
        { command: "bold", icon: "mdi-format-bold", title: "غامق" },
        { command: "italic", icon: "mdi-format-italic", title: "مائل" },
        { command: "underline", icon: "mdi-format-underline", title: "تسطير" },
        {
          command: "insertUnorderedList",
          icon: "mdi-format-list-bulleted",
          title: "قائمة نقطية",
        },
        {
          command: "insertOrderedList",
          icon: "mdi-format-list-numbered",
          title: "قائمة مرقمة",
        },
        {
          command: "removeFormat",
          icon: "mdi-format-clear",
          title: "مسح التنسيق",
        },
      ],
    };
  },
  computed: {
    iconColor() {
      return this.$store.getters.isDark ? "#E0E0E0" : "#424242";
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.$nextTick(() => {
          if (!this.$refs.editor) return;
          const html = this.normalizeValue(newVal || "");
          if (this.$refs.editor.innerHTML !== html) {
            this.$refs.editor.innerHTML = html;
          }
        });
      },
    },
  },
  methods: {
    normalizeValue(val) {
      // Treat plain-text legacy values as text by escaping and converting newlines.
      // Detected HTML is passed through untouched.
      if (!/<[a-z][\s\S]*>/i.test(val)) {
        return val.replace(/\n/g, "<br>");
      }
      return val;
    },

    execCommand(command, value = null) {
      this.$refs.editor.focus();
      // execCommand is deprecated but remains the most lightweight way to
      // implement basic rich-text editing without third-party dependencies.
      document.execCommand(command, false, value);
      this.onInput();
    },

    onInput() {
      const html = this.$refs.editor.innerHTML;
      // Treat a lone <br> (left over after deleting all content) as empty
      const cleaned = html === "<br>" ? "" : html;
      this.$emit("input", cleaned);
    },

    onPaste(event) {
      // Force plain-text paste so users can't smuggle in arbitrary styles/scripts
      event.preventDefault();
      const text = (event.clipboardData || window.clipboardData).getData("text");
      document.execCommand("insertText", false, text);
    },
  },
};
</script>

<style scoped>
.simple-editor {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #ffffff;
  overflow: hidden;
}

.simple-editor.is-dark {
  background: #1e1e1e;
  border-color: #424242;
}

.simple-editor__toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 4px 6px;
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
}

.simple-editor.is-dark .simple-editor__toolbar {
  background: #2c2c2c;
  border-bottom-color: #424242;
}

.simple-editor__btn {
  background: transparent;
  border: none;
  padding: 4px 8px;
  border-radius: 3px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
}

.simple-editor__btn:hover {
  background: #eceff1;
}

.simple-editor.is-dark .simple-editor__btn:hover {
  background: #3a3a3a;
}

.simple-editor__btn:focus {
  outline: none;
  background: #e0e0e0;
}

.simple-editor.is-dark .simple-editor__btn:focus {
  background: #4a4a4a;
}

.simple-editor__content {
  min-height: 96px;
  padding: 12px 14px;
  outline: none;
  font-size: 14px;
  line-height: 1.6;
  color: #333333;
  direction: rtl;
  text-align: right;
  word-break: break-word;
}

.simple-editor.is-dark .simple-editor__content {
  color: #e0e0e0;
}

.simple-editor__content:empty::before {
  content: attr(data-placeholder);
  color: #9e9e9e;
  pointer-events: none;
}

.simple-editor__content ul,
.simple-editor__content ol {
  padding-right: 24px;
  padding-left: 0;
  margin: 4px 0;
}
</style>
